<template>
  <div class="px-5 pt-2 h-full overflow-auto pb-5">
    <!-- IF / ELSE IF branch cards -->
    <div v-for="(expression, exprIndex) in expressions" :key="expression.id" class="mt-3 border border-solid border-stroke rounded-lg overflow-hidden">
      <!-- Card header -->
      <div class="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-solid border-stroke">
        <span class="text-11 font-600 uppercase tracking-wide text-gray-500">{{ getBranchLabel(exprIndex) }}</span>
        <IconButton v-if="exprIndex > 0" icon="close" :size="4" dense class="cursor-pointer text-gray-400" @click="removeExpression(exprIndex)" />
      </div>

      <!-- Card body -->
      <div class="px-3 py-3">
        <div v-for="(condition, condIndex) in expression.conditions" :key="condition.id">
          <!-- AND/OR connector between conditions -->
          <div v-if="condIndex > 0" class="flex items-center my-2">
            <div class="inline-flex text-11 font-500 border border-solid border-stroke rounded overflow-hidden">
              <button
                class="px-2 py-0.5 transition-colors"
                :class="condition.logic === 'AND' ? 'bg-purple-100 text-purple-700' : 'bg-white text-gray-400 hover:bg-gray-50'"
                @click="setConditionLogic(exprIndex, condIndex, 'AND')"
              >
                AND
              </button>
              <button
                class="px-2 py-0.5 border-l border-solid border-stroke transition-colors"
                :class="condition.logic === 'OR' ? 'bg-purple-100 text-purple-700' : 'bg-white text-gray-400 hover:bg-gray-50'"
                @click="setConditionLogic(exprIndex, condIndex, 'OR')"
              >
                OR
              </button>
            </div>
          </div>

          <!-- Condition row: left | operator | right | delete -->
          <div class="flex items-start gap-2">
            <!-- Left operand -->
            <div class="flex-1 min-w-0">
              <div class="flex gap-0.5 mb-1">
                <button
                  class="text-11 px-1.5 py-0.5 rounded font-500 transition-colors"
                  :class="condition.left.type === 'variable' ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:bg-gray-100'"
                  @click="setOperandType(exprIndex, condIndex, 'left', 'variable')"
                >
                  Variable
                </button>
                <button
                  class="text-11 px-1.5 py-0.5 rounded font-500 transition-colors"
                  :class="condition.left.type === 'value' ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:bg-gray-100'"
                  @click="setOperandType(exprIndex, condIndex, 'left', 'value')"
                >
                  Value
                </button>
              </div>
              <VariableSelect
                v-if="condition.left.type === 'variable'"
                :modelValue="condition.left.value"
                :showCreate="false"
                dense
                @update:modelValue="
                  (v) => {
                    condition.left.value = v;
                    emitPayload();
                  }
                "
              />
              <Input
                v-else
                :modelValue="condition.left.value || ''"
                placeholder="Enter value..."
                class="w-full"
                @update:modelValue="
                  (v) => {
                    condition.left.value = v;
                    emitPayload();
                  }
                "
              />
            </div>

            <!-- Operator — invisible spacer mirrors the toggle-button row so inputs align -->
            <div class="w-36 flex-shrink-0 flex flex-col">
              <div class="mb-1 invisible" aria-hidden="true">
                <span class="text-11 px-1.5 py-0.5">x</span>
              </div>
              <FormSelect
                :modelValue="condition.operator"
                :options="operatorOptions"
                :reduce="(o) => o.value"
                :clearable="false"
                :searchable="false"
                appendToBody
                @option:selected="(o) => updateOperator(exprIndex, condIndex, o.value)"
              />
            </div>

            <!-- Right operand -->
            <div class="flex-1 min-w-0" v-if="!isUnaryOperator(condition.operator)">
              <div class="flex gap-0.5 mb-1">
                <button
                  class="text-11 px-1.5 py-0.5 rounded font-500 transition-colors"
                  :class="condition.right.type === 'variable' ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:bg-gray-100'"
                  @click="setOperandType(exprIndex, condIndex, 'right', 'variable')"
                >
                  Variable
                </button>
                <button
                  class="text-11 px-1.5 py-0.5 rounded font-500 transition-colors"
                  :class="condition.right.type === 'value' ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:bg-gray-100'"
                  @click="setOperandType(exprIndex, condIndex, 'right', 'value')"
                >
                  Value
                </button>
              </div>
              <VariableSelect
                v-if="condition.right.type === 'variable'"
                :modelValue="condition.right.value"
                :showCreate="false"
                dense
                @update:modelValue="
                  (v) => {
                    condition.right.value = v;
                    emitPayload();
                  }
                "
              />
              <Input
                v-else
                :modelValue="condition.right.value || ''"
                placeholder="Enter value..."
                class="w-full"
                @update:modelValue="
                  (v) => {
                    condition.right.value = v;
                    emitPayload();
                  }
                "
              />
            </div>
            <div v-else class="flex-1" />

            <!-- Delete condition row — same invisible spacer for alignment -->
            <div class="flex-shrink-0 flex flex-col">
              <div class="mb-1 invisible" aria-hidden="true">
                <span class="text-11 px-1.5 py-0.5">x</span>
              </div>
              <IconButton v-if="expression.conditions.length > 1" icon="close" :size="4" dense class="cursor-pointer text-gray-400" @click="removeCondition(exprIndex, condIndex)" />
              <div v-else class="w-6" />
            </div>
          </div>
        </div>

        <button class="mt-2 text-12 text-purple-600 hover:text-purple-800" @click="addCondition(exprIndex)">+ Add condition</button>
      </div>
    </div>

    <!-- ELSE card (always present as the final fallback) -->
    <div class="mt-3 border border-solid border-stroke rounded-lg overflow-hidden">
      <div class="px-3 py-2 bg-gray-50">
        <span class="text-11 font-600 uppercase tracking-wide text-gray-500">ELSE</span>
      </div>
    </div>

    <!-- Add ELSE IF -->
    <button class="mt-3 text-12 text-purple-600 hover:text-purple-800" @click="addExpression">+ Add ELSE IF</button>
  </div>
</template>

<script>
import uuidv4 from '../../../helpers/uuid';
import debounce from '../../../helpers/debounce';

const OPERATOR_OPTIONS = [
  { label: 'equals (==)', value: '==' },
  { label: 'not equals (!=)', value: '!=' },
  { label: 'greater than (>)', value: '>' },
  { label: 'greater or equal (>=)', value: '>=' },
  { label: 'less than (<)', value: '<' },
  { label: 'less or equal (<=)', value: '<=' },
  { label: 'contains', value: 'contains' },
  { label: 'not contains', value: 'not_contains' },
  { label: 'starts with', value: 'starts_with' },
  { label: 'ends with', value: 'ends_with' },
  { label: 'is empty', value: 'is_empty' },
  { label: 'is not empty', value: 'is_not_empty' },
];

const UNARY_OPERATORS = new Set(['is_empty', 'is_not_empty']);

function newCondition(logic = null) {
  return {
    id: uuidv4(),
    left: { type: 'variable', value: null },
    operator: '==',
    right: { type: 'value', value: '' },
    logic,
  };
}

function newExpression() {
  return {
    id: uuidv4(),
    conditions: [newCondition()],
    payload: [],
  };
}

function migrateExpression(expr) {
  if ('conditions' in expr) return expr;
  // Migrate old free-text format to structured format
  return {
    id: expr.id || uuidv4(),
    conditions: [newCondition()],
    payload: expr.payload || [],
  };
}

export default {
  name: 'ConditionConfig',
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      evaluation: {},
      operatorOptions: OPERATOR_OPTIONS,
    };
  },
  computed: {
    expressions() {
      return this.evaluation.payload?.expressions || [];
    },
  },
  methods: {
    getBranchLabel(index) {
      return index === 0 ? 'IF' : 'ELSE IF';
    },
    isUnaryOperator(op) {
      return UNARY_OPERATORS.has(op);
    },
    setConditionLogic(exprIndex, condIndex, logic) {
      this.expressions[exprIndex].conditions[condIndex].logic = logic;
      this.emitPayload();
    },
    setOperandType(exprIndex, condIndex, side, type) {
      this.expressions[exprIndex].conditions[condIndex][side] = { type, value: null };
      this.emitPayload();
    },
    updateOperator(exprIndex, condIndex, value) {
      const condition = this.expressions[exprIndex].conditions[condIndex];
      condition.operator = value;
      if (UNARY_OPERATORS.has(value)) {
        condition.right = { type: 'value', value: null };
      }
      this.emitPayload();
    },
    addCondition(exprIndex) {
      this.expressions[exprIndex].conditions.push(newCondition('AND'));
      this.emitPayload();
    },
    removeCondition(exprIndex, condIndex) {
      this.expressions[exprIndex].conditions.splice(condIndex, 1);
      this.emitPayload();
    },
    addExpression() {
      this.evaluation.payload.expressions.push(newExpression());
      this.emitPayload();
    },
    removeExpression(index) {
      this.evaluation.payload.expressions.splice(index, 1);
      this.emitPayload();
    },
    // eslint-disable-next-line
    emitPayload: debounce(function () {
      this.$emit('update', JSON.parse(JSON.stringify(this.evaluation)));
    }, 300),
  },
  watch: {
    item: {
      handler() {
        if (this.item) {
          const copy = JSON.parse(JSON.stringify(this.item));
          if (copy.payload?.expressions) {
            copy.payload.expressions = copy.payload.expressions.map(migrateExpression);
          }
          const wasEmpty = !copy.payload?.expressions?.length;
          if (wasEmpty) {
            copy.payload.expressions = [newExpression()];
          }
          this.evaluation = copy;
          if (wasEmpty) {
            this.$nextTick(() => this.$emit('update', JSON.parse(JSON.stringify(this.evaluation))));
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
